import { CSSProperties, ReactNode } from "react";
import { IonButton, IonText } from "@ionic/react";
import { MdEdit, MdMail, MdPhone } from "react-icons/md";
// FIXME: @deprecate https://linear.app/swing-education/issue/SCHOOLS-18/deprecate-swing-assets-package
import { subPhotoPlaceholderPNG } from "swing-assets";
import { Qualifications } from "swing-components";
import { phoneNumberFormatter, phoneNumberParser } from "swing-utils";

import { translate } from "~utils";

import { SwingHeroImageWrapper } from "../SwingHeroImageWrapper";
import styles from "./SubProfile.module.css";

const ExperienceTag = {
  EXP_TAG_K_5: "K-5",
  EXP_TAG_ADULT: "Adult",
  EXP_TAG_PRE_K: "Pre-K",
  EXP_TAG_9_12: "9-12",
  EXP_TAG_6_8: "6-8",
} as const;

export type ExperienceTagKey = keyof typeof ExperienceTag;

const ExperienceType = {
  SUBSTITUTE_TEACHER: "Substitute Teacher",
  ADMINISTRATOR: "Administrator",
  CLASSROOM_LEAD: "Classroom Lead",
  SMALL_GROUP_INSTRUCTION: "Small Group Instruction",
  ONE_ON_ONE: "One on One",
  CLASSROOM_SUPPORT: "Classroom Support",
} as const;

export type ExperienceTypeKey = keyof typeof ExperienceType;

// TODO: matches shape of Sub data but should be
// updated once API types can be integrated
type SubProfileProps = {
  isEdit?: boolean;
  id: string;
  profileImageUrl?: string;
  name: string;
  phoneNumber?: string;
  email?: string;
  about?: string;
  qualifications?: string[];
  completedSkillBuilders?: string[];
  isSwingHero?: boolean;
  stats?: {
    daysWorked?: number;
    schoolsWorked?: number;
    timesPreferred?: number;
    positiveReviews?: number;
  };
  experience?: {
    startDate: string;
    endDate?: string;
    organization: string;
    role: string;
    tags: ExperienceTagKey[];
    type: ExperienceTypeKey;
  }[];
  education?: string[];
  feedback?: { schoolName: string; content: string }[];
  onEditPhoneNumber?: () => void;
  onEditAvatar?: () => void;
};

type Contact = Pick<
  SubProfileProps,
  | "id"
  | "profileImageUrl"
  | "name"
  | "phoneNumber"
  | "email"
  | "onEditAvatar"
  | "onEditPhoneNumber"
  | "isSwingHero"
>;

export function SubProfile(props: SubProfileProps) {
  const {
    isEdit,
    about,
    education,
    experience,
    feedback,
    qualifications,
    stats,
    isSwingHero,
    completedSkillBuilders,
    ...contact
  } = props;

  const hasStats =
    !!stats?.daysWorked ||
    !!stats?.schoolsWorked ||
    !!stats?.timesPreferred ||
    !!stats?.positiveReviews;

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileContent}>
        <div className={styles.profileContentSection}>
          <Contact isEdit={isEdit} contact={contact} isSwingHero={isSwingHero} />
        </div>
        <div className={styles.profileContentSection}>
          <AboutMe about={about} />
        </div>
        {/* "floats" right on desktop */}
        {qualifications && qualifications.length > 0 && (
          <div className={styles.profileContentSectionFloatRight}>
            <Qualifications
              qualifications={qualifications}
              isSwingHero={isSwingHero}
              skillBuilderCompletedTasks={completedSkillBuilders}
            />
          </div>
        )}
        {hasStats && (
          <div className={styles.profileContentSection}>
            <Stats stats={stats} />
          </div>
        )}
        <div className={styles.profileContentSection}>
          <Experience experience={experience} />
        </div>
        <div className={styles.profileContentSection}>
          <Education education={education} />
        </div>
        <div className={styles.profileContentSection}>
          <Feedback feedback={feedback} />
        </div>
      </div>
    </div>
  );
}

type ProfileImageProps = {
  isSwingHero?: boolean;
  children: ReactNode;
};

function ProfileImage({ isSwingHero, children }: ProfileImageProps) {
  if (!isSwingHero) {
    return <>{children}</>;
  }

  return <SwingHeroImageWrapper>{children}</SwingHeroImageWrapper>;
}

export type ContactProps = Pick<SubProfileProps, "isEdit" | "isSwingHero"> & {
  contact: Omit<Contact, "id">;
};

export function Contact(props: ContactProps) {
  const { isEdit, contact, isSwingHero } = props;
  const { profileImageUrl, name, email, phoneNumber, onEditAvatar, onEditPhoneNumber } = contact;

  const parsedPhoneNumber = phoneNumberParser({ phoneNumber: phoneNumber || "" });
  const formattedPhoneNumber = phoneNumberFormatter({
    phoneNumber: parsedPhoneNumber,
  })?.value;

  const imageStyles = {
    width: 68,
    height: 68,
    borderRadius: "100%",
    objectFit: "cover",
  } satisfies CSSProperties;

  return (
    <StyledCard backgroundColor="transparent">
      <div className={styles.contactContent}>
        <div css={{ width: 80, position: "relative" }}>
          {profileImageUrl ? (
            <ProfileImage isSwingHero={isSwingHero}>
              <img src={profileImageUrl} alt={name} css={imageStyles} />
            </ProfileImage>
          ) : (
            <ProfileImage isSwingHero={isSwingHero}>
              <img
                src={subPhotoPlaceholderPNG}
                alt={translate("SUB_PROFILE_CONTACT_IMAGE_ALT")}
                css={imageStyles}
              />
            </ProfileImage>
          )}
          {isEdit && (
            <IonButton
              size="small"
              shape="round"
              onClick={() => onEditAvatar?.()}
              css={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: 20,
                height: 20,
                borderRadius: "50%",
                "--padding-bottom": 0,
                "--padding-end": 0,
                "--padding-start": 0,
                "--padding-top": 0,
                // to reduce size for MD icon
                fontSize: 0,
              }}
            >
              <MdEdit css={{ width: 12, height: 12 }} color="var(--ion-color-primary-contrast)" />
            </IonButton>
          )}
        </div>
        {name || email || phoneNumber ? (
          <div
            css={{ display: "flex", flexDirection: "column" }}
            className={styles.contactContentInfo}
          >
            {name && (
              <div
                css={{ fontSize: "var(--swing-font-xl)", fontWeight: "var(--swing-font-medium)" }}
              >
                <IonText css={{ fontFamily: "var(--swing-font-family-headers)" }}>{name}</IonText>
              </div>
            )}
            {phoneNumber && (
              <div css={{ marginTop: "var(--spacing-sm)" }}>
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-base)",
                  }}
                >
                  <MdPhone size={24} />
                  <IonText>{formattedPhoneNumber}</IonText>
                  {isEdit && (
                    <IonButton fill="clear" size="small" onClick={() => onEditPhoneNumber?.()}>
                      <MdEdit css={{ width: 12, height: 12 }} color="var(--ion-color-primary)" />
                      <IonText css={{ paddingLeft: "var(--spacing-base)" }}>
                        {translate("SUB_PROFILE_CONTACT_EDIT_BUTTON")}
                      </IonText>
                    </IonButton>
                  )}
                </div>
              </div>
            )}
            {email && (
              <div css={{ marginTop: "var(--spacing-sm)" }}>
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-base)",
                  }}
                >
                  <MdMail size={24} />
                  <IonText>{email}</IonText>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </StyledCard>
  );
}

function AboutMe({ about }: { about: SubProfileProps["about"] }) {
  return (
    <StyledCard borderRadius="8px 8px 0px 0px">
      <div>
        <h2 css={{ marginTop: 0, fontSize: "var(--swing-font-xl)" }}>
          {translate("SUB_PROFILE_ABOUT_HEADER")}
        </h2>
        <p css={{ marginBottom: 0, lineHeight: "var(--swing-line-height-normal)" }}>
          {about ? (
            about
          ) : (
            <span css={{ fontStyle: "italic" }}>{translate("SUB_PROFILE_NO_INFORMATION")}</span>
          )}
        </p>
      </div>
    </StyledCard>
  );
}

function Stats({ stats }: { stats: SubProfileProps["stats"] }) {
  // add <br /> to separate words onto separate lines
  function formatLabel(wordStr: string) {
    return wordStr
      .split(" ")
      .map((word, idx, array) => {
        if (idx < array.length - 1) {
          return `${word} <br />`;
        }
        return word;
      })
      .join(" ");
  }

  return (
    <StyledCard>
      <div>
        <h2 css={{ marginTop: 0, fontSize: "var(--swing-font-xl)" }}>
          {translate("SUB_PROFILE_SWING_EXPERIENCE_HEADER")}
        </h2>
        <ul
          css={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 60,
            margin: 0,
            marginTop: "var(--spacing-2xl)",
            padding: 0,
            paddingLeft: "var(--spacing-xl)",
            paddingRight: "var(--spacing-xl)",
          }}
        >
          {!!stats?.daysWorked && (
            <li
              css={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <span
                css={{
                  fontSize: "var(--swing-font-2xl)",
                  fontWeight: "var(--swing-font-medium)",
                }}
              >
                {stats.daysWorked}
              </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatLabel(translate("SUB_PROFILE_SWING_EXPERIENCE_DAYS_WORKED")),
                }}
                css={{
                  fontSize: "var(--swing-font-sm)",
                  lineHeight: "var(--swing-line-height-normal)",
                }}
              />
            </li>
          )}
          {!!stats?.schoolsWorked && (
            <li
              css={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <span
                css={{
                  fontSize: "var(--swing-font-2xl)",
                  fontWeight: "var(--swing-font-medium)",
                }}
              >
                {stats.schoolsWorked}
              </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatLabel(translate("SUB_PROFILE_SWING_EXPERIENCE_SCHOOLS_WORKED")),
                }}
                css={{
                  fontSize: "var(--swing-font-sm)",
                  lineHeight: "var(--swing-line-height-normal)",
                }}
              />
            </li>
          )}
          {!!stats?.timesPreferred && (
            <li
              css={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <span
                css={{
                  fontSize: "var(--swing-font-2xl)",
                  fontWeight: "var(--swing-font-medium)",
                }}
              >
                {stats.timesPreferred}
              </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatLabel(translate("SUB_PROFILE_SWING_EXPERIENCE_TIMES_PREFERRED")),
                }}
                css={{
                  fontSize: "var(--swing-font-sm)",
                  lineHeight: "var(--swing-line-height-normal)",
                }}
              />
            </li>
          )}
          {!!stats?.positiveReviews && (
            <li
              css={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <span
                css={{
                  fontSize: "var(--swing-font-2xl)",
                  fontWeight: "var(--swing-font-medium)",
                }}
              >
                {stats.positiveReviews}
              </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: formatLabel(translate("SUB_PROFILE_SWING_EXPERIENCE_POSITIVE_REVIEWS")),
                }}
                css={{
                  fontSize: "var(--swing-font-sm)",
                  lineHeight: "var(--swing-line-height-normal)",
                }}
              />
            </li>
          )}
        </ul>
      </div>
    </StyledCard>
  );
}

function Experience({ experience }: { experience: SubProfileProps["experience"] }) {
  return (
    <StyledCard>
      <div>
        <h2 css={{ marginTop: 0, fontSize: "var(--swing-font-xl)" }}>
          {translate("SUB_PROFILE_RELEVANT_WORK_EXPERIENCE_HEADER")}
        </h2>
        {experience && experience.length > 0 ? (
          <ul css={{ margin: 0, padding: 0, paddingLeft: "var(--spacing-lg)" }}>
            {experience.map((exp, idx) => {
              const startDate = new Date(exp.startDate);
              const endDate = new Date(exp?.endDate || "");
              return (
                <li key={idx} css={{ marginTop: "var(--spacing-3xl)" }}>
                  <div className={styles.experienceContent}>
                    <div className={styles.experienceContentDate}>
                      <span>
                        {`${startDate.getMonth() + 1}/${startDate.getFullYear()}`}
                        {`${
                          exp.endDate
                            ? ` - ${endDate.getMonth() + 1}/${endDate.getFullYear()}`
                            : " - Present"
                        }`}
                      </span>
                    </div>
                    <div className={styles.experienceContentInfo}>
                      <div>
                        <span
                          css={{ fontWeight: "var(--swing-font-medium)" }}
                        >{`${exp.organization}`}</span>
                        <span>, {exp.role}</span>
                      </div>
                      <div
                        className={styles.experienceContentRole}
                        css={{ fontStyle: "italic" }}
                      >{`${ExperienceType[exp.type]}, (${exp.tags.map((tag, idx, array) =>
                        idx !== 0 && idx <= array.length - 1
                          ? ` ${ExperienceTag[tag]}`
                          : ExperienceTag[tag],
                      )})`}</div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <span css={{ fontStyle: "italic" }}>{translate("SUB_PROFILE_NO_INFORMATION")}</span>
        )}
      </div>
    </StyledCard>
  );
}

function Education({ education }: { education: SubProfileProps["education"] }) {
  return (
    <StyledCard>
      <div>
        <h2 css={{ marginTop: 0, fontSize: "var(--swing-font-xl)" }}>
          {translate("SUB_PROFILE_EDUCATION_HEADER")}
        </h2>
        {education && education.length > 0 ? (
          <ul css={{ margin: 0, padding: 0, paddingLeft: "var(--spacing-lg)" }}>
            {education.map((edu) => (
              <li key={edu} className={styles.educationList}>
                {edu}
              </li>
            ))}
          </ul>
        ) : (
          <span css={{ fontStyle: "italic" }}>{translate("SUB_PROFILE_NO_INFORMATION")}</span>
        )}
      </div>
    </StyledCard>
  );
}

function Feedback({ feedback }: { feedback: SubProfileProps["feedback"] }) {
  return (
    <StyledCard>
      <div>
        <h2 css={{ marginTop: 0, fontSize: "var(--swing-font-xl)" }}>
          {translate("SUB_PROFILE_FEEDBACK_FROM_SCHOOLS_HEADER")}
        </h2>
        <div>
          {feedback && feedback.length > 0 ? (
            feedback.map(({ content, schoolName }, idx) => (
              <>
                {content.length > 0 && (
                  <blockquote
                    key={idx}
                    className={styles.feedbackQuote}
                    css={{
                      backgroundColor: "var(--ion-color-light)",
                      borderRadius: 8,
                      padding: "var(--spacing-lg)",
                    }}
                  >
                    <div
                      css={{
                        display: "flex",
                        flexDirection: "column",
                        paddingLeft: "var(--spacing-lg)",
                        paddingRight: "var(--spacing-lg)",
                      }}
                    >
                      <p css={{ margin: 0 }}>
                        <span>
                          <q>
                            <i>{content}</i>
                          </q>
                        </span>
                      </p>
                      <footer
                        css={{
                          color: "var(--ion-color-medium-shade)",
                          marginTop: "var(--spacing-sm)",
                        }}
                      >
                        {`- ${schoolName}`}
                      </footer>
                    </div>
                  </blockquote>
                )}
              </>
            ))
          ) : (
            <span css={{ fontStyle: "italic" }}>{translate("SUB_PROFILE_NO_INFORMATION")}</span>
          )}
        </div>
      </div>
    </StyledCard>
  );
}

function StyledCard({
  backgroundColor = "var(--ion-color-primary-contrast)",
  borderRadius,
  children,
}: {
  backgroundColor?: string;
  borderRadius?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={styles.styledCard}
      css={{
        padding: "var(--spacing-2xl)",
        backgroundColor,
        borderRadius,
      }}
    >
      {children}
    </div>
  );
}
